import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Inbox, Clock } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { api } from "../../lib/api";
import { fmtDatePT, fmtTimePT } from "../../lib/time";

/**
 * The cross-event pending-approvals inbox (replaces the legacy per-series
 * queue): every pending registration — 1:1 session slot requests and
 * approval-mode general events — in one list, oldest first. Approving a slot
 * request books the slot; declining releases it.
 */
export default function AdminRequests() {
  const [rows, setRows] = useState(null);

  const load = () => api.get("/admin/requests").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const decide = async (row, status) => {
    try {
      await api.post(`/events/${row.event_id}/registrations/${row.registration_id}/decision`, { status });
      toast.success(status === "going"
        ? `${row.name} approved${row.slot_id ? " — slot booked" : ""}`
        : `${row.name} declined${row.slot_id ? " — slot released" : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error === "slot_taken"
        ? "That slot was already booked — decline this request or move it"
        : (e.response?.data?.detail || "Failed"));
    }
  };

  return (
    <AdminShell
      testId="admin-requests"
      eyebrow="Requests"
      title="Pending approvals"
      subtitle="Every pending registration across all events — 1:1 session requests and approval-mode events."
    >
        <div className="max-w-4xl">
          {rows === null ? (
            <div className="skeleton h-40" />
          ) : rows.length === 0 ? (
            <div className="card-hair p-10 text-center text-sm text-muted-foreground" data-testid="requests-empty">
              All caught up — no pending requests.
            </div>
          ) : (
            <div className="card-hair overflow-hidden" data-testid="requests-list">
              {rows.map((r) => (
                <div key={r.registration_id} className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0" data-testid={`request-${r.registration_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{r.name} <span className="text-xs text-muted-foreground font-normal">· {r.email}</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <Link to={`/admin/events/${r.event_id}/manage`} className="underline hover:text-foreground">{r.event_title}</Link>
                      <Clock size={11} />
                      {r.slot_id
                        ? <span className="font-mono">{fmtDatePT(r.slot_start_at)} · {fmtTimePT(r.slot_start_at)}–{fmtTimePT(r.slot_end_at)} PT</span>
                        : <span className="font-mono">{fmtDatePT(r.event_start_at)} · {fmtTimePT(r.event_start_at)} PT</span>}
                      {r.format === "slots" && <span className="chip chip-purple text-[10px]">1:1 slot</span>}
                    </div>
                  </div>
                  <button onClick={() => decide(r, "going")} className="h-9 px-3 flex items-center gap-1.5 rounded-lg text-xs font-medium text-white" style={{ background: "hsl(var(--vd-green))" }} data-testid={`request-approve-${r.registration_id}`}>
                    <Check size={13} /> Approve
                  </button>
                  <button onClick={() => decide(r, "declined")} className="h-9 px-3 flex items-center gap-1.5 rounded-lg hover:bg-secondary text-xs" style={{ color: "hsl(var(--vd-coral))" }} data-testid={`request-decline-${r.registration_id}`}>
                    <X size={13} /> Decline
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
    </AdminShell>
  );
}
